"use client";

import {
  AlertOutlined,
  AppstoreOutlined,
  AuditOutlined,
  BarChartOutlined,
  BookOutlined,
  BugOutlined,
  DatabaseOutlined,
  ExperimentOutlined,
  InboxOutlined,
  SearchOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { Typography } from "antd";
import Link from "next/link";
import { type ReactNode, useMemo } from "react";
import { resolveHomeTiles, type HomeQueueCounts, type HomeTileDef } from "@/lib/home/tiles";
import { useT } from "@/lib/i18n/I18nProvider";
import { useSession } from "@/providers/SessionProvider";
import styles from "./HomeTileGrid.module.css";

const { Text } = Typography;

const ICONS: Record<string, ReactNode> = {
  experiment: <ExperimentOutlined />,
  alert: <AlertOutlined />,
  biolog: <AppstoreOutlined />,
  search: <SearchOutlined />,
  inbox: <InboxOutlined />,
  book: <BookOutlined />,
  chart: <BarChartOutlined />,
  phageChart: <BarChartOutlined />,
  bug: <BugOutlined />,
  migration: <DatabaseOutlined />,
  settings: <SettingOutlined />,
  dna: <ExperimentOutlined />,
  audit: <AuditOutlined />,
};

type HomeTileGridProps = {
  queues?: HomeQueueCounts;
};

function TileCard({ tile, queues }: { tile: HomeTileDef; queues?: HomeQueueCounts }) {
  const t = useT();
  return (
    <div
      style={{
        borderRadius: 10,
        border: `1px solid ${tile.accent}33`,
        background: tile.background,
        padding: "14px 14px 12px",
        minHeight: 168,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 10,
        boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
        minWidth: 0,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span
          aria-hidden
          style={{
            width: 36,
            height: 36,
            borderRadius: 8,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#fff",
            color: tile.accent,
            fontSize: 18,
            border: `1px solid ${tile.accent}44`,
            flexShrink: 0,
          }}
        >
          {ICONS[tile.icon] ?? <ExperimentOutlined />}
        </span>
        <Text strong style={{ color: tile.accent, fontSize: 16 }}>
          {t(tile.titleKey)}
        </Text>
      </div>
      <ul
        style={{
          listStyleType: "none",
          marginTop: 0,
          marginBottom: 0,
          paddingLeft: 0,
          flexGrow: 1,
        }}
      >
        {tile.links.map((link) => {
          const count = link.queueKey && queues ? queues[link.queueKey] : undefined;
          return (
            <li key={link.labelKey} style={{ marginBottom: 6 }}>
              <Link
                href={link.href}
                style={{
                  color: "#0f172a",
                  textDecoration: "none",
                  fontSize: 13,
                  display: "inline-flex",
                  gap: 6,
                  alignItems: "baseline",
                }}
              >
                <span style={{ color: tile.accent }}>•</span>
                <span>
                  {t(link.labelKey)}
                  {typeof count === "number" ? (
                    <Text type="secondary" style={{ marginLeft: 6 }}>
                      ({count})
                    </Text>
                  ) : null}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function HomeTileGrid({ queues }: HomeTileGridProps) {
  const { hasPermission } = useSession();
  const tiles = useMemo(() => resolveHomeTiles(hasPermission), [hasPermission]);

  return (
    <div className={styles.grid}>
      {tiles.map((tile) => (
        <TileCard key={tile.id} tile={tile} queues={queues} />
      ))}
    </div>
  );
}
